import { createSelector } from 'reselect';
import { GAME_MODE, DEEP_LINK_GAME_MODE, DEEP_LINK_MODES, PROGRAMS } from 'root/lib/constants';
import GameIds from 'a/hub/data/GameIds';
import { getDevDirectGameId } from 'lib/utilityFunctions';
import { getIsStaffLogin } from 'a/selectors/persistStateSelectors';
import { SEASON, REDIRECT_THRESHOLD, BACKPACK_LIMIT_OVERRIDE } from '../data/constants';
import * as fromQuestWorkflow from './questWorkflowSelectors';
import * as fromWorkshop from './workshopSelectors';
import * as fromAssignment from './assignmentLinkSelectors';
import STEP_TYPE from '../data/StepTypes';

export * from './studentProgressSelectors';
export * from './modalSystemSelectors';
export * from './assignmentLinkSelectors';
export * from './curiosoSelectors';
export * from './metaGameSelectors';

export const getSeason = state => state.questWorkflowReducer.season;

export const getIsPlacementTool = state =>
  state.selectedActivity.activity === GameIds.PLACEMENT_TOOL;

export const getIsSkillScan = state =>
  state.selectedActivity.activity === GameIds.SKILL_SCAN;

export const getIsSeason3 = createSelector(
  [getSeason],
  season => season === SEASON.GRADE_4_5,
);

export const getChallengeQuestGames = createSelector(
  [
    getIsSeason3,
    fromQuestWorkflow.getChallengeQuestSteps,
    fromWorkshop.getChallengeQuestGames,
  ],
  (isSeason3, season1And2Games, season3Games) => {
    // TO-DO remove filtering once ereader issue is fixed
    const filterFn = game => game.type === STEP_TYPE.GAME;
    return isSeason3 ? season3Games.filter(filterFn) : season1And2Games;
  },
);

export const getIsChallengeQuestComplete = createSelector(
  [
    getIsSeason3,
    fromQuestWorkflow.getIsChallengeQuestComplete,
    fromWorkshop.getIsChallengeQuestComplete,
  ],
  (isSeason3, isSeason1Or2Complete, isSeason3Complete) => (isSeason3 ? isSeason3Complete : isSeason1Or2Complete),
);

export const getCurrentQuestId = ({
  questWorkflowReducer: { season, currentActiveQuestId },
  workshopReducer: { currentQuestId },
}) => (season === SEASON.GRADE_4_5 ? currentQuestId : currentActiveQuestId);

export const getIsQuestWorkflow = (state) => state.selectedActivity.isQuestWorkflow;

export const getIsQuestInDemoMode = (state) => state.questWorkflowReducer.isQuestInDemoMode;

export const getIsSideQuest = (state) => !!state.selectedActivity.params?.isSideQuest;

export const getIsDeepLink = (state) => !!state.selectedActivity.params?.isLink;

export const getDeepLinkProgram = (state) => (
  getIsDeepLink(state) ? state.selectedActivity.params?.deepLinkProgram : null
);

export const getIsDeepLinkELA = (state) => getDeepLinkProgram(state) === PROGRAMS.ELA;

export const getGameMode = (state) => {
  const isLink = getIsDeepLink(state);
  const isQuestWorkflow = getIsQuestWorkflow(state);
  const isQuestInDemoMode = getIsQuestInDemoMode(state);
  const isDevDirectAccess = !!getDevDirectGameId();

  if (isDevDirectAccess) {
    return GAME_MODE.PRACTICE;
  }
  if (isLink) {
    const { deepLinkMode } = state.selectedActivity.params;
    // deep links are given client side, so verify staff is accessing
    if (deepLinkMode === DEEP_LINK_MODES.TEACHER && !getIsStaffLogin(state)) {
      return GAME_MODE.PRACTICE_LINK;
    }
    return DEEP_LINK_GAME_MODE[deepLinkMode] || GAME_MODE.PRACTICE_LINK;
  }
  if (isQuestWorkflow) {
    return GAME_MODE.QUEST;
  }
  if (isQuestInDemoMode) {
    return GAME_MODE.DEMO;
  }
  return GAME_MODE.PRACTICE;
};

export const getShouldShowQuestContinuePopupSeason1and2 = (state) => {
  const isGrade45 = state.questWorkflowReducer.season === SEASON.GRADE_4_5;
  const { isQuestWorkflow } = state.selectedActivity;
  const { isSideQuest } = state.selectedActivity.params;
  const isShowingRewards = state.questNavigationReducer.showAugmentationNavigationScreen;
  const {
    showSingleStepMasteredScreen,
    showAllStepsMasteredScreen,
    showThreeFailuresScreen,
  } = state.questNavigationReducer;
  const interstitialFlag = showSingleStepMasteredScreen || showAllStepsMasteredScreen || showThreeFailuresScreen;
  return (!isGrade45 &&
  isQuestWorkflow &&
  interstitialFlag &&
  !isSideQuest &&
  !isShowingRewards);
};

export const getCuriosoCustomizationLimit = (state) => {
  if (fromQuestWorkflow.getAreAllQuestsAndChallengesComplete(state)) {
    return null;
  }
  return state.curiosoReducer.curiosoCustomizationLimit;
};

export const getBackpackLimit = (state) => {
  if (
    fromQuestWorkflow.getAreAllQuestsAndChallengesComplete(state) ||
    sessionStorage.getItem(BACKPACK_LIMIT_OVERRIDE) === 'true'
  ) {
    return null;
  }

  return REDIRECT_THRESHOLD;
};

// Captions follow the teacher-set preference for every grade, including Grade 4-5.
// (Previously gated off for Grade 4-5 per AQUA-37198, with an `areCaptionsForced`
// aqua-admin override from AQUA-39360 — both removed now that the teacher setting is working.)
export const getAreCaptionsEnabled = (state) => state.questWorkflowReducer.captions;

export const getAreCaptionsAvailable = (state) =>
  getAreCaptionsEnabled(state) && !state.questWorkflowReducer.areCaptionsBlocked;

export const getGameFailures = createSelector(
  [
    getGameMode,
    getIsSeason3,
    fromWorkshop.getGameFailures,
    fromQuestWorkflow.getGameFailures,
    fromAssignment.getAssignmentLinkFailures,
  ],
  (gameMode, isSeason3, workshopGameFailures, bookertonGameFailures, assignmentFailures) => {
    if (gameMode === GAME_MODE.ASSIGNMENT_LINK) {
      return assignmentFailures;
    }

    if (gameMode === GAME_MODE.QUEST) {
      return isSeason3 ? workshopGameFailures : bookertonGameFailures;
    }

    return {};
  },
);
